import { ApiAccessSettingsComponent } from './settings/api-access-settings/api-access-settings.component';
import { AppSettingsComponent } from './settings/app-settings/app-settings.component';
import { BotsSettingsComponent } from './settings/bots-settings/bots-settings.component';
import { EditProfileComponent } from './settings/edit-profile/edit-profile.component';
import { QueueAdminComponent } from './settings/queue-admin/queue-admin.component';
import { UsersComponent } from './settings/users/users.component';
import { NgModule } from '@angular/core';
import {
  RouterModule,
  Routes,
  UrlMatchResult,
  UrlSegment,
} from '@angular/router';

import { authGuard } from '@core';
import { featureCheckGuard } from '@core/guards/feature.guard';
import { roleGuard } from '@core/guards/role.guard';
import { userRedirectGuard } from '@core/guards/user-redirect.guard';
import { environment } from '@env/environment';
import { AdminLayoutComponent } from '@theme/admin-layout/admin-layout.component';
import { AuthLayoutComponent } from '@theme/auth-layout/auth-layout.component';

import { AnalyticsComponent } from './analytics/analytics.component';
import { ApisComponent } from './apis/apis.component';
import { BotsComponent } from './bots/bots.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ProjectsComponent } from './projects/projects.component';
import { ResultsComponent } from './results/results.component';
import { Error403Component } from './sessions/403.component';
import { Error404Component } from './sessions/404.component';
import { Error500Component } from './sessions/500.component';
import { ForgotPasswordComponent } from './sessions/forgot-password/forgot-password.component';
import { LoginComponent } from './sessions/login/login.component';
import { RegisterComponent } from './sessions/register/register.component';
import { SSOHandlerComponent } from './sessions/sso-handler/sso-handler.component';
import { SourcesComponent } from './sources/sources.component';

const resultsRouteMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (segments.length === 1 && segments[0].path === 'results') {
    return { consumed: segments };
  }

  if (
    segments.length === 3 &&
    segments[0].path === 'results' &&
    segments[1].path === 'conversation' &&
    segments[2].path
  ) {
    return {
      consumed: segments,
      posParams: {
        id: segments[2],
      },
    };
  }

  return null;
};

const routes: Routes = [
  { path: '', component: AdminLayoutComponent, canActivate: [authGuard], canActivateChild: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: DashboardComponent, canActivate: [userRedirectGuard] },
      { path: '403', component: Error403Component },
      { path: '404', component: Error404Component },
      { path: '500', component: Error500Component },
      { path: 'sources/batch-upload', canActivate: [roleGuard], loadChildren: () => import('../modules/batch-upload/batch-upload.module').then(m => m.BatchUploadModule), },
      {
        path: 'sources',
        component: SourcesComponent,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./sources/views/sources-empty-view.component').then((m) => m.SourcesEmptyViewComponent),
          },
          {
            path: 'folder/:folderId',
            loadComponent: () =>
              import('./sources/views/sources-folder-view.component').then((m) => m.SourcesFolderViewComponent),
          },
          {
            path: 'folder/:folderId/source/:sourceId',
            loadComponent: () =>
              import('./sources/views/source-details-view.component').then((m) => m.SourceDetailsViewComponent),
          },
          {
            path: 'folder/:folderId/source/:sourceId/annotations',
            loadComponent: () =>
              import('./sources/views/source-annotations-view.component').then(
                (m) => m.SourceAnnotationsViewComponent
              ),
          },
          {
            path: 'annotations/:sourceId',
            loadComponent: () =>
              import('./sources/views/source-annotations-view.component').then(
                (m) => m.SourceAnnotationsViewComponent
              ),
          },
          {
            path: 'materials',
            loadComponent: () =>
              import('./sources/views/materials-view.component').then((m) => m.MaterialsViewComponent),
          },
          {
            path: 'context',
            loadComponent: () =>
              import('./sources/views/context-view.component').then((m) => m.ContextViewComponent),
          },
        ],
      },
      { matcher: resultsRouteMatcher, component: ResultsComponent },
      { path: 'bots', component: BotsComponent, children: [{ path: ':id', component: BotsComponent }], },
      { path: 'projects', component: ProjectsComponent, children: [{ path: ':id', component: ProjectsComponent }], },
      { path: 'apis', component: ApisComponent },
      { path: 'slr', canActivate: [featureCheckGuard('slr')], loadChildren: () => import('../features/slr/slr.module').then(m => m.SlrModule), },
      { path: 'chat', canActivate: [featureCheckGuard('chat')], loadChildren: () => import('../features/chat/chat.module').then(m => m.ChatModule), },
      { path: 'dossier', canActivate: [featureCheckGuard('dossier')], loadChildren: () => import('../features/dossier/dossier.module').then(m => m.DossierModule), },
      { path: 'lmr', canActivate: [featureCheckGuard('lmr')], loadChildren: () => import('../features/lmr/lmr.module').then(m => m.LmrModule), },
      { path: 'validator', canActivate: [featureCheckGuard('validator')], loadChildren: () => import('../features/lmr/lmr.module').then(m => m.LmrModule), },
      { path: 'ab-compare', canActivate: [featureCheckGuard('ab-compare')], loadChildren: () => import('../features/ab-compare/ab-compare.module').then(m => m.AbCompareModule), },
      { path: 'profile/settings', component: EditProfileComponent },
      { path: 'profile/api-settings', component: ApiAccessSettingsComponent },
      { path: 'profile/bots-settings', component: BotsSettingsComponent },
      { path: 'app-settings', component: AppSettingsComponent },
      { path: 'app-users', component: UsersComponent, canActivate: [roleGuard] },
      { path: 'app-queue', component: QueueAdminComponent, canActivate: [roleGuard] },
      { path: 'analytics', component: AnalyticsComponent, canActivate: [roleGuard] },
      {
        path: 'styleguide',
        canActivate: [roleGuard],
        loadComponent: () =>
          import('./styleguide/styleguide-shell.component').then(m => m.StyleguideShellComponent),
      },
    ],
  },
  { path: 'auth', component: AuthLayoutComponent, children: [
      { path: 'login', component: LoginComponent },
      { path: 'register', component: RegisterComponent },
      { path: 'forgot-password', component: ForgotPasswordComponent },
      { path: 'token-handler', component: SSOHandlerComponent },
    ],
  },
  { path: 'orb-demo', loadComponent: () => import('./orb-demo/orb-demo.component').then(m => m.OrbDemoComponent) },
  { path: 'shimmer-test', loadComponent: () => import('../features/conversation/components/shimmer-test/shimmer-test.component').then(m => m.ShimmerTestComponent) },
  { path: 'chunker-test', loadComponent: () => import('../features/conversation/components/shimmer-test/chunker-test.component').then(m => m.ChunkerTestComponent) },
  { path: '**', redirectTo: 'dashboard' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: environment.useHash,
      onSameUrlNavigation: 'ignore',
    }),
  ],
  exports: [RouterModule],
})
export class RoutesRoutingModule {}
